import { mod } from '@updater/angular-common';
import { KEYCODE } from '../../../../constants';

export const DIRECTIVE_NAME = 'updMenuFocus';
const TAB_INDEX = 'tabindex';

const FOCUS_MOVEMENT = {
  [KEYCODE.UP]: -1,
  [KEYCODE.DOWN]: 1,
};

// Determine if an element is visible.
// https://stackoverflow.com/a/21696585/89594.
const isVisible = el => !!el.offsetParent;

/**
 * Manages mouse and keyboard focus for a menu per ARIA specs (see comments below).
 *
 * Usage:
 * <ul upd-menu-focus="$ctrl.isOpen">
 *   <li tabindex="-1">1</li>
 *   <li tabindex="-1">2</li>
 *   <li tabindex="-1">3</li>
 * </ul>
 *
 * Accepts an expression that toggles the directive's functionality.
 * Once toggled, it will capture focus within its element. All child elements with
 * `tabindex="-1"` will be focusable using arrow keys using an implementation of
 * "roving" keyboard focus.
 */
export function menuFocus($parse, $timeout) {
  'ngInject';
  return {
    restrict: 'A',
    link: ($scope, $element, $attr) => {
      let previouslyFocused;

      const attrValue = $attr[DIRECTIVE_NAME];
      const el = $element[0];

      $scope.$watch($parse(attrValue), newValue => {
        if (newValue) {
          captureFocus();
          el.addEventListener('keydown', handleKeyDown);
          el.addEventListener('click', handleClick);
        } else {
          el.removeEventListener('keydown', handleKeyDown);
          el.removeEventListener('click', handleClick);
          restoreFocus();
        }
      });

      function captureFocus() {
        previouslyFocused = document.activeElement;
        // Wait for other blur/focus events that might interrupt capturing.
        $timeout(() => moveRovingFocus(el, 0));
      }

      function restoreFocus() {
        const focusedElement = document.activeElement;

        // Don't attempt to restore focus if something else already moved it.
        if (el.contains(focusedElement)) {
          focusedElement.blur();

          // `previouslyFocused` can be null (since `document.activeElement` can
          // be `null`), though we're only seeing this in IE11.
          // https://developer.mozilla.org/en-US/docs/Web/API/DocumentOrShadowRoot/activeElement#Value
          if (previouslyFocused) {
            previouslyFocused.focus();
          }
        }
      }

      // Partial implementation of ARIA Menu Keyboard Interaction spec:
      // https://www.w3.org/TR/wai-aria-practices-1.1/#menu
      function handleKeyDown(event) {
        switch (event.keyCode) {
          case KEYCODE.DOWN:
          case KEYCODE.UP:
            moveRovingFocus(el, FOCUS_MOVEMENT[event.keyCode]);
            // Prevent arrow keys from scrolling page.
            event.preventDefault();
            break;
          case KEYCODE.RETURN:
          case KEYCODE.ESC:
            // Wait so we don't interrupt the browser's native RETURN handler
            // (which triggers click behavior on buttons/anchors/etc).
            $timeout(() => restoreFocus());
            break;
        }
      }

      function handleClick(event) {
        const targetTabIndex = event.target.getAttribute(TAB_INDEX);

        // When a menu item is clicked (activated, same as RETURN), the menu
        // is considered closed and should restore focus to the element from
        // which it was captured.
        if (['-1', '0'].indexOf(targetTabIndex) !== -1) {
          restoreFocus();
        }
      }
    },
  };
}

// Implementation of roving tabindex per:
// https://www.w3.org/TR/wai-aria-practices-1.1/#kbd_roving_tabindex
function moveRovingFocus(el, amount) {
  const tabindexs = Array.from(el.querySelectorAll(`[${TAB_INDEX}]`)).filter(
    isVisible
  );

  if (tabindexs.length === 0) {
    return;
  }

  const currentIndex = tabindexs.findIndex(
    x => x.getAttribute(TAB_INDEX) === '0'
  );
  let nextIndex;

  if (currentIndex !== -1) {
    const current = tabindexs[currentIndex];
    current.setAttribute(TAB_INDEX, '-1');
    nextIndex = mod(currentIndex + amount, tabindexs.length);
  } else {
    nextIndex = 0;
  }

  const next = tabindexs[nextIndex];
  next.setAttribute(TAB_INDEX, '0');
  next.focus();
}
